import React, { useEffect, useState } from 'react';
import { Users, Loader2, RefreshCw, Search, X, CheckCircle2, Eye, FileText, Inbox, ChevronLeft, ChevronRight } from 'lucide-react';
import { submissionsApi, SubmissionRecord, newslettersApi, NewsletterRecord, CreatorFilter } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
/** Parse Form_Payload JSON safely */
function parsePayload(raw: string): Record<string, any> | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
/** Extract email from payload */
function extractEmail(payload: Record<string, any> | null): string {
  if (!payload) return '-';
  return payload.email || payload.Email || payload.EMAIL || payload.e_mail || '-';
}
/** Extract a display name from payload */
function extractName(payload: Record<string, any> | null): string {
  if (!payload) return '';
  const first = payload.firstName || payload.first_name || payload.FirstName || payload.name || '';
  const last = payload.lastName || payload.last_name || payload.LastName || '';
  return [first, last].filter(Boolean).join(' ');
}
const PAGE_SIZE = 200;
export function Subscriptions() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [newsletters, setNewsletters] = useState<NewsletterRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionRecord | null>(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showPagination = totalCount > PAGE_SIZE;
  const paginationPanel = showPagination ? <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3" data-id="element-6524" data-mp="3neujqtpu9yxb">
      <div className="text-sm text-gray-600" data-id="element-6525" data-mp="3ieyekr5eo1g6">
        Showing {(currentPage - 1) * PAGE_SIZE + 1}–
        {Math.min(currentPage * PAGE_SIZE, totalCount)} of{' '}
        {totalCount.toLocaleString()} confirmed subscribers
      </div>
      <div className="flex items-center gap-2" data-id="element-6526" data-mp="38ovmqua0stjf">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6527" data-mp="34njyas2vuect">
          <ChevronLeft className="h-4 w-4" data-id="element-6528" data-mp="2ps3x833vxi6x" />
          Previous
        </button>
        <span className="text-sm text-gray-700 px-2" data-id="element-6529" data-mp="sx7sj6me5f4b">
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6530" data-mp="o0hd8lieunjy">
          Next
          <ChevronRight className="h-4 w-4" data-id="element-6531" data-mp="1a84mcgt1dulc" />
        </button>
      </div>
    </div> : null;
  const buildScopeFilters = (): CreatorFilter[] => {
    const filters: CreatorFilter[] = [];
    filters.push({
      field: 'Team',
      value: currentWorkspace.name
    });
    if (currentSubRegion) {
      filters.push({
        field: 'Region',
        value: currentSubRegion
      });
    }
    // Always filter to Confirmed only on this page
    filters.push({
      field: 'Status',
      value: 'Confirmed'
    });
    return filters;
  };
  const fetchData = async (page: number = 1, search: string = activeSearch) => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const scopeFilters = buildScopeFilters();
      // Search filter (multi-field)
      const searchFilter: CreatorFilter[] = search.trim() ? [{
        fields: ['Subscriber_Email', 'search_text'],
        value: search.trim()
      }] : [];
      const dataFilters: CreatorFilter[] = [...scopeFilters, ...searchFilter];
      const [subsResult, nlResult] = await Promise.all([submissionsApi.list({
        offset: offset > 0 ? offset : undefined,
        filters: dataFilters
      }), newslettersApi.list()]);
      setSubmissions(subsResult.data);
      setTotalCount(subsResult.total_count);
      setNewsletters(nlResult);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };
  const handleSearch = () => {
    const trimmed = searchQuery.trim();
    setActiveSearch(trimmed);
    fetchData(1, trimmed);
  };
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    fetchData(1, '');
  };
  useEffect(() => {
    fetchData(1, activeSearch);
  }, [currentWorkspace.name, currentSubRegion]);
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchData(page);
  };
  const getNewsletterName = (newsletterId: string): string => {
    const nl = newsletters.find(n => n.Newsletter_ID === newsletterId);
    return nl?.Newsletter || newsletterId || '-';
  };
  return <div className="space-y-6" data-id="element-6532" data-mp="3bq7q56hucnb">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-6533" data-mp="3cj3kb3wzhqo">
        <div className="min-w-0" data-id="element-6534" data-mp="8b2tsnpjjfou">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6535" data-mp="886xt4x97131">
            Subscriptions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6536" data-mp="3qia54x0vp2sp">
            Confirmed subscribers who completed double opt-in.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-6537" data-mp="20z230pmjueog">
          <button onClick={() => fetchData(currentPage)} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-6538" data-mp="210vau88wf4y1">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6539" data-mp="1m7tbuc300l97" />
          </button>
        </div>
      </div>

      {/* Search & count */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-6540" data-mp="2ezla9uj6xgpk">
        <div className="flex gap-2 flex-1 max-w-sm" data-id="element-6541" data-mp="29z71ubdog4r0">
          <div className="relative flex-1" data-id="element-6542" data-mp="29xnkehgabnnn">
            <input type="text" placeholder="Search by email, name, or newsletter..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') handleSearch();
          }} className="pl-3 pr-8 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-6543" data-mp="25y598de9mqy0" />
            {searchQuery && <button onClick={handleClearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-6544" data-mp="3f705yeyefcgk">
                <X className="h-3.5 w-3.5" data-id="element-6545" data-mp="3jzb35a1r8k5f" />
              </button>}
          </div>
          <button onClick={handleSearch} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" title="Search" data-id="element-6546" data-mp="3oyw39nsnuyjq">
            <Search className="h-4 w-4" data-id="element-6547" data-mp="23j88x2uq4f7n" />
          </button>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500" data-id="element-6548" data-mp="23l1f6h1fcxf4">
          <CheckCircle2 className="h-4 w-4 text-green-500" data-id="element-6549" data-mp="28j2zt6ei14sp" />
          <span data-id="element-6550" data-mp="ea7l5z8mmqcb">
            {totalCount.toLocaleString()} confirmed subscriber
            {totalCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Top Pagination */}
      {paginationPanel}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6551" data-mp="nyxvbydtlkme">
        {loading && submissions.length === 0 ? <div className="p-12 text-center" data-id="element-6552" data-mp="j390u9gnqtwy">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-6553" data-mp="3rg7pwctk5kb3" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-6554" data-mp="3nfnt5acwciph">
              Loading subscriptions...
            </p>
          </div> : submissions.length === 0 ? <div className="p-12 text-center" data-id="element-6555" data-mp="57ojvjkhols6">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-6556" data-mp="a542f8580y4" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-6557" data-mp="d13y93jwzp7">
              {activeSearch ? 'No matching subscribers' : 'No confirmed subscribers yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-6558" data-mp="2tngg708sv3si">
              {activeSearch ? 'Try adjusting your search.' : 'Subscribers who confirm their double opt-in email will appear here.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-6559" data-mp="2kocy9zldcufl">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-6560" data-mp="2plldpsf4ukd5">
              <thead className="bg-gray-50" data-id="element-6561" data-mp="2pnz962dq5yl8">
                <tr data-id="element-6562" data-mp="3db7c4dnokvk3">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6563" data-mp="3dcqs035fd4lc">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6564" data-mp="33lw9n9vaup56">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6565" data-mp="38immlobt54md">
                    Newsletter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6566" data-mp="4wfkp777a73r">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6567" data-mp="3jfn6236jz7zq">
                    Confirmed At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6568" data-mp="35i6yupgfna6x">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6569" data-mp="2vpj4e1cxdibd">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6570" data-mp="2ltpukpqlc2xa">
                {submissions.map(sub => {
              const payload = parsePayload(sub.Form_Payload);
              const email = extractEmail(payload);
              const name = extractName(payload);
              return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-6571" data-mp="2hpqeg898364g">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6572" data-mp="1y5sx66xqiqxc">
                        <span className="text-sm font-medium text-gray-900 truncate block max-w-[220px]" data-id="element-6573" data-mp="1t5wvwfgf9dlh">
                          {email}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6574" data-mp="2342vurio49np">
                        <span className="text-sm text-gray-600 truncate block max-w-[150px]" data-id="element-6575" data-mp="235xhy95oe7td">
                          {name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6576" data-mp="2c495217c2nh2">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600 truncate max-w-[180px]" data-id="element-6577" data-mp="2qsdox8i4h51f">
                          <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-6578" data-mp="2luudshgq18dp" />
                          {getNewsletterName(sub.Newsletter_ID)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6579" data-mp="2uwtsv486e2e0">
                        {sub.Region || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6580" data-mp="1xfw7vl6musje">
                        {sub.Confirmed_At1 ? new Date(sub.Confirmed_At1).toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6581" data-mp="2b82mh2u50rgq">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-6582" data-mp="2b7u7n5egfc2w">
                          <CheckCircle2 className="h-3 w-3" data-id="element-6583" data-mp="2l605nbx53y2h" />
                          Confirmed
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-6584" data-mp="2p5iugis30tal">
                        <button onClick={() => setSelectedSubmission(sub)} className="text-blue-600 hover:text-blue-900 text-sm inline-flex items-center gap-1" data-id="element-6585" data-mp="qu1z5hq7tgaa">
                          <Eye className="h-3.5 w-3.5" data-id="element-6586" data-mp="ijuwuz1ss516" />
                          View
                        </button>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Bottom Pagination */}
      {paginationPanel}

      {/* Payload Modal */}
      {selectedSubmission && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6587" data-mp="11acbjzpsp03j">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6588" data-mp="11avqd7k5f8dc">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedSubmission(null)} data-id="element-6589" data-mp="x764wqyrt6wj" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-6590" data-mp="3hyzxser37838">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-6591" data-mp="3czwuozuo3bxw">
                <div data-id="element-6592" data-mp="14qf7qtg24r6a">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-6593" data-mp="14pvy000k993c">
                    Subscriber Details
                  </h3>
                  <p className="text-xs text-gray-500 font-mono mt-0.5" data-id="element-6594" data-mp="19iyn9vyb1wdq">
                    ID: {selectedSubmission.ID}
                  </p>
                </div>
                <button onClick={() => setSelectedSubmission(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-md" data-id="element-6595" data-mp="zsvu9vr6oife">
                  <X className="h-5 w-5" data-id="element-6596" data-mp="1sh1caaqurtbj" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-6597" data-mp="1eh0cyzq8kty6">
                <div className="flex items-center justify-between" data-id="element-6598" data-mp="1eg8lo13o9idp">
                  <span className="text-sm font-medium text-gray-700" data-id="element-6599" data-mp="1jf1uhg80kl7j">
                    Status
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-6600" data-mp="300cvhxndatl">
                    <CheckCircle2 className="h-3 w-3" data-id="element-6601" data-mp="3voiiu3o36z5d" />
                    Confirmed
                  </span>
                </div>

                <div className="flex items-center justify-between" data-id="element-6602" data-mp="3rmomg7kxchb0">
                  <span className="text-sm font-medium text-gray-700" data-id="element-6603" data-mp="9id8nlhivgv2">
                    Newsletter
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-6604" data-mp="4k1y18jel2bs">
                    {getNewsletterName(selectedSubmission.Newsletter_ID)}
                  </span>
                </div>

                {(() => {
              const payload = parsePayload(selectedSubmission.Form_Payload);
              if (!payload) return <div className="text-sm text-red-500" data-id="element-6605" data-mp="3hxn4wcyqdrux">
                        Could not parse payload
                      </div>;
              return <div className="space-y-2" data-id="element-6606" data-mp="2u5pxcy2bdlzm">
                      <h4 className="text-sm font-medium text-gray-700" data-id="element-6607" data-mp="2p7y6m7deo5x0">
                        Form Data
                      </h4>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200" data-id="element-6608" data-mp="t5l1td1x0d14">
                        {Object.entries(payload).filter(([key]) => key !== 'newsletter_id' && key !== 'submitted_at').map(([key, value]) => <div key={key} className="flex items-center justify-between px-3 py-2" data-id="element-6609" data-mp="a8tmtfteok2v">
                              <span className="text-xs font-medium text-gray-500" data-id="element-6610" data-mp="j63yh2a4yb8b">
                                {key}
                              </span>
                              <span className="text-sm text-gray-900 text-right max-w-[250px] truncate" data-id="element-6611" data-mp="1unc2hmgvdpby">
                                {String(value)}
                              </span>
                            </div>)}
                      </div>
                      {payload.submitted_at && <div className="flex items-center justify-between pt-2" data-id="element-6612" data-mp="24s24ttg6qvgp">
                          <span className="text-xs text-gray-400" data-id="element-6613" data-mp="1zz94dhrr29af">
                            Submitted at
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-6614" data-mp="1vwcpo25byxbr">
                            {new Date(payload.submitted_at).toLocaleString()}
                          </span>
                        </div>}
                    </div>;
            })()}

                <details className="group" data-id="element-6615" data-mp="1h1g8v0jgh1n2">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600" data-id="element-6616" data-mp="1d031un4mmsek">
                    View raw JSON
                  </summary>
                  <pre className="mt-2 bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto max-h-48" data-id="element-6617" data-mp="3bd3dfes5gggn">
                    {JSON.stringify(parsePayload(selectedSubmission.Form_Payload), null, 2) || selectedSubmission.Form_Payload}
                  </pre>
                </details>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-6618" data-mp="3guga14vx6o5k">
                <button onClick={() => setSelectedSubmission(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6619" data-mp="3ltsx9qqpdx8o">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}